import moment from "moment";
import { uuid } from "uuidv4";
import { TFunction } from "i18next";

import {
    APP_URL,
    PERIPHERALS_URL,
    MONETIC_URL,
    CURRENCIES_URL,
    APP_ID,
    DATE_FORMAT,
    HOUR_FORMAT,
    DeviceType,
    ActionType,
} from "./constants";
import {
    FetchGlobalFn,
    BasePayload,
    ApplicationPayload,
    DissociateDevicePayload,
    AssociatePeripheralsPayload,
    MoneticSettingPayload,
} from "./types";

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

function basePayload(params: {
    userID: string;
    franchiseID: string;
    operatorID: string;
    shopId: string;
}): BasePayload {
    return {
        userId: params.userID,
        uuidUser: localStorage.getItem("uuidUser"),
        shopId: params.shopId,
        franchiseId: params.franchiseID,
        operatorId: params.operatorID,
    };
}

function postJSON(fetchGlobal: FetchGlobalFn, url: string, payload: unknown) {
    return fetchGlobal(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
    });
}

function deleteJSON(fetchGlobal: FetchGlobalFn, url: string, payload: unknown) {
    return fetchGlobal(url, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
    });
}

function timestamp() {
    return {
        modified_at: moment().format(DATE_FORMAT),
        hour: moment().format(HOUR_FORMAT),
    };
}

// ---------------------------------------------------------------------------
// Application CRUD
// ---------------------------------------------------------------------------

export type AddApplicationResult = {
    /** Backward-compat: true when at least one shop failed. */
    error: boolean;
    /** Human-readable message: first failure when partial, generic on success. */
    message: string;
    /** Total number of target shops the dispatch fanned out to. */
    totalShops: number;
    /** Shops where the POST succeeded. */
    successShopIds: string[];
    /** Shops where the POST failed (rejected promise or `error: true` body). */
    failedShopIds: string[];
};

/**
 * Add a POS application to one or more shops. The BE endpoint accepts a
 * single shop per call (unlike Kiosk's variant that unwraps an array body),
 * so we fan out N parallel POSTs — same end-result as Kiosk multi-shop add.
 *
 * Uses `Promise.allSettled` so a single failing shop does not abort the
 * others. The returned `AddApplicationResult` lets the caller distinguish
 * total success / partial / total failure and tell the user precisely
 * which shops did not get the new app — avoiding blind re-submits that
 * would create duplicates on the shops that already succeeded.
 */
export async function addApplication(
    fetchGlobal: FetchGlobalFn,
    params: {
        userID: string;
        franchiseID: string;
        operatorID: string;
        oneShop: boolean;
        shopID: string;
        shopIds: string[];
        designation: string;
        ip: string;
        remark: string;
        codeCaisse: string;
    }
): Promise<AddApplicationResult> {
    const targetShopIds = params.oneShop ? [params.shopID] : params.shopIds;
    if (targetShopIds.length === 0) {
        return {
            error: true,
            message: "No shop selected",
            totalShops: 0,
            successShopIds: [],
            failedShopIds: [],
        };
    }

    const buildPayload = (shopId: string): ApplicationPayload => {
        const id = uuid();
        return {
            ...basePayload({
                userID: params.userID,
                franchiseID: params.franchiseID,
                operatorID: params.operatorID,
                shopId,
            }),
            type: ActionType.ADD,
            data: {
                [APP_ID]: {
                    id_application: APP_ID,
                    data_app: {
                        [id]: {
                            id_application_children: id,
                            shopId,
                            printer: {},
                            designation: params.designation,
                            ip: params.ip,
                            note: params.remark,
                            codeCaisse: params.codeCaisse,
                            isArchived: false,
                            ...timestamp(),
                        },
                    },
                },
            },
        };
    };

    const settled = await Promise.allSettled(
        targetShopIds.map((shopId) =>
            postJSON(fetchGlobal, APP_URL, buildPayload(shopId))
        )
    );

    const successShopIds: string[] = [];
    const failedShopIds: string[] = [];
    let firstErrorMessage = "";

    settled.forEach((outcome, index) => {
        const shopId = targetShopIds[index];
        if (outcome.status === "rejected") {
            failedShopIds.push(shopId);
            if (!firstErrorMessage) {
                firstErrorMessage =
                    outcome.reason?.message ?? String(outcome.reason);
            }
            return;
        }
        const body = outcome.value as {
            error?: boolean;
            message?: string;
        } | null;
        if (body?.error) {
            failedShopIds.push(shopId);
            if (!firstErrorMessage) firstErrorMessage = body.message ?? "";
            return;
        }
        successShopIds.push(shopId);
    });

    return {
        error: failedShopIds.length > 0,
        message:
            failedShopIds.length === 0
                ? "successfully registered"
                : firstErrorMessage || "failed registration",
        totalShops: targetShopIds.length,
        successShopIds,
        failedShopIds,
    };
}

export async function editApplication(
    fetchGlobal: FetchGlobalFn,
    params: {
        userID: string;
        franchiseID: string;
        operatorID: string;
        oneShop: boolean;
        shopID: string;
        shopId: string;
        appId: string;
        printer: Record<string, unknown>;
        designation: string;
        ip: string;
        remark: string;
        codeCaisse: string;
    }
) {
    const payload: ApplicationPayload = {
        ...basePayload({
            userID: params.userID,
            franchiseID: params.franchiseID,
            operatorID: params.operatorID,
            shopId: params.oneShop ? params.shopID : params.shopId,
        }),
        data: {
            [APP_ID]: {
                id_application: APP_ID,
                data_app: {
                    [params.appId]: {
                        id_application_children: params.appId,
                        shopId: params.oneShop ? params.shopID : params.shopId,
                        printer: params.printer,
                        designation: params.designation,
                        ip: params.ip,
                        note: params.remark,
                        codeCaisse: params.codeCaisse,
                        isArchived: false,
                        ...timestamp(),
                    },
                },
            },
        },
    };

    return postJSON(fetchGlobal, APP_URL, payload);
}

export async function archiveApplications(
    fetchGlobal: FetchGlobalFn,
    params: {
        userID: string;
        franchiseID: string;
        operatorID: string;
        oneShop: boolean;
        shopID: string;
        selectedRows: Record<string, any>[];
        t: TFunction;
    }
) {
    const ts = timestamp();
    const dataApp = params.selectedRows
        .filter((row) => row.appId !== undefined)
        .reduce((acc: any, row: any) => {
            acc[row.appId] = {
                id_application_children: row.appId,
                shopId: params.oneShop ? params.shopID : row.shopId,
                printer: row.printer,
                designation: row[params.t("Application")],
                ip: row[params.t("Address IP")],
                note: row[params.t("Note")],
                isArchived: true,
                ...ts,
            };
            return acc;
        }, {});

    const payload: ApplicationPayload = {
        ...basePayload({
            userID: params.userID,
            franchiseID: params.franchiseID,
            operatorID: params.operatorID,
            shopId: "",
        }),
        type: ActionType.ARCHIVE,
        data: {
            [APP_ID]: {
                id_application: APP_ID,
                data_app: dataApp,
            },
        },
    };

    return postJSON(fetchGlobal, APP_URL, payload);
}

export async function unarchiveApplications(
    fetchGlobal: FetchGlobalFn,
    params: {
        userID: string;
        franchiseID: string;
        operatorID: string;
        oneShop: boolean;
        shopID: string;
        selectedRows: Record<string, any>[];
        t: TFunction;
    }
) {
    const ts = timestamp();
    const dataApp = params.selectedRows
        .filter((row: any) => row.appId !== undefined)
        .reduce((acc: any, row: any) => {
            acc[row.appId] = {
                id_application_children: row.appId,
                shopId: params.oneShop ? params.shopID : row.shopId,
                printer: row.printer,
                designation: row[params.t("Application")],
                ip: row[params.t("Address IP")],
                note: row[params.t("Note")],
                isArchived: false,
                ...ts,
            };
            return acc;
        }, {});

    const payload: ApplicationPayload = {
        ...basePayload({
            userID: params.userID,
            franchiseID: params.franchiseID,
            operatorID: params.operatorID,
            shopId: "",
        }),
        type: ActionType.UNARCHIVE,
        data: {
            [APP_ID]: {
                id_application: APP_ID,
                data_app: dataApp,
            },
        },
    };

    return postJSON(fetchGlobal, APP_URL, payload);
}

// ---------------------------------------------------------------------------
// Device dissociate
// ---------------------------------------------------------------------------

export function getDeviceTypeForApi(peripheralType: string): string {
    return peripheralType === DeviceType.CASH_MANAGEMENT
        ? "cashManagement"
        : peripheralType;
}

export async function dissociateDevice(
    fetchGlobal: FetchGlobalFn,
    params: {
        userID: string;
        franchiseID: string;
        operatorID: string;
        oneShop: boolean;
        shopID: string;
        selectedRow: Record<string, any>;
    }
) {
    const deviceType = getDeviceTypeForApi(params.selectedRow.peripheralType);
    const url = `${APP_URL}/${deviceType}`;

    const payload: DissociateDevicePayload = {
        ...basePayload({
            userID: params.userID,
            franchiseID: params.franchiseID,
            operatorID: params.operatorID,
            shopId: params.oneShop ? params.shopID : params.selectedRow.shopId,
        }),
        appId: APP_ID,
        appId_children: params.selectedRow.appId,
        designation: params.selectedRow.applicationDesignation,
        peripheralName: params.selectedRow["Périphérique"],
        peripheralId: params.selectedRow.peripheralId,
        ...timestamp(),
    };

    return deleteJSON(fetchGlobal, url, payload);
}

// ---------------------------------------------------------------------------
// Peripheral association
// ---------------------------------------------------------------------------

export async function associatePeripherals(
    fetchGlobal: FetchGlobalFn,
    params: {
        userID: string;
        franchiseID: string;
        operatorID: string;
        oneShop: boolean;
        shopID: string;
        rowData: Record<string, any>;
        printerSelected: any[];
        monetics: any[];
        cashManagements: any[];
        t: TFunction;
        // Multi-association: the modal applies to >1 app at once. The user
        // is expected to *add* peripherals without touching what each app
        // already has, so we skip the disassociate diff and only emit the
        // `associate` payload. Per-app calls still benefit from the
        // existing `isNewPeripheral` filter, which prevents duplicate adds
        // for peripherals already in the app's subRows.
        // Required (not optional) so every call site declares its intent
        // explicitly — defaulting to `false` would silently drop existing
        // associations for callers that forget to pass it.
        isMulti: boolean;
    }
) {
    const row = params.rowData[0];
    const shopId = params.oneShop ? params.shopID.toString() : row.shopId;
    const subRows: any[] = row.subRows || [];
    const isMulti = params.isMulti;

    // Find peripherals to disassociate. In multi-mode we intentionally
    // return an empty diff so existing associations stay untouched.
    const findDisassociated = (type: string, newList: any[]) => {
        if (isMulti) return [];
        const oldIds = subRows
            .filter((item: any) => item.peripheralType === type)
            .map((item: any) => item.peripheralId);
        const newIds = newList.map((item: any) => item["Id"]);
        return oldIds.filter(
            (id: string) => newIds.indexOf(id) === -1 && id !== undefined
        );
    };

    const disassocPrinterIds = findDisassociated(
        DeviceType.PRINTER,
        params.printerSelected
    );
    const disassocMoneticIds = findDisassociated(
        DeviceType.MONETIC,
        params.monetics
    );
    const disassocCashIds = findDisassociated(
        DeviceType.CASH_MANAGEMENT,
        params.cashManagements
    );

    // Build disassociate maps
    const buildDisassociateMap = (ids: string[], idKey: string) => {
        const map: Record<string, any> = {};
        ids.forEach((id: string) => {
            let name = "";
            subRows.forEach((el: any) => {
                if (el.peripheralId === id) name = el[params.t("Device")];
            });
            map[id] = { [idKey]: id, designation: name };
        });
        return map;
    };

    const printersToDisassociate = buildDisassociateMap(
        disassocPrinterIds,
        "id_printer"
    );
    const moneticsToDisassociate = buildDisassociateMap(
        disassocMoneticIds,
        "id_monetic"
    );
    const cashManagementToDisassociate = buildDisassociateMap(
        disassocCashIds,
        "id_cash_management"
    );

    // Build associate maps
    const isNewPeripheral = (id: string, disassocIds: string[]) =>
        id !== undefined &&
        disassocIds.indexOf(id) === -1 &&
        subRows.every((elt: any) => elt.peripheralId !== id);

    const printersToAssociate: Record<string, any> = {};
    params.printerSelected.forEach((el: any) => {
        if (isNewPeripheral(el["Id"], disassocPrinterIds)) {
            printersToAssociate[el["Id"]] = {
                id_printer: el["Id"],
                designation: el["Nom de l'imprimante"],
                mark: el["Marque"],
                port: el["port"],
                note: el["Remarque"],
                status: "in line",
                state: "to configure",
                path: el["path"],
                font_size: el["Taille police"],
                edit_date: "",
                type: el["type"],
                add_date: "",
                speed: el["speed"],
                ip: el["ip"],
                port_series: el["port_series"],
                ticket_width: el["ticket_width"],
                tickets: el["tickets"],
                code: el["code"],
                general: el["general"] || {},
                customer_ticket: el["customer_ticket"] || {},
                Kitchen_ticket: el["Kitchen_ticket"] || {},
                modelKitchenTicket: el["modelKitchenTicket"] || {},
                divers: el["divers"] || {},
                isArchived:
                    el["isArchived"] !== undefined ? el["isArchived"] : false,
            };
        }
    });

    const moneticsToAssociate: Record<string, any> = {};
    params.monetics.forEach((el: any) => {
        if (isNewPeripheral(el["Id"], disassocMoneticIds)) {
            moneticsToAssociate[el["Id"]] = {
                id_monetic: el["Id"],
                // Display fields are keyed on the selected row by the *translated*
                // column header. Read them through t() — exactly as the monetic
                // form mapper does — so designation / mark / reference / note are
                // not lost when the UI language is not French.
                designation: el[params.t("Designation")],
                mark: el[params.t("Mark")],
                port: el["port"],
                note: el[params.t("Remark")],
                status: "in line",
                state: "to configure",
                path: el["url"],
                android: el["android"],
                merchant_code: el["merchant_code"],
                // The monetic record stores the TPE name under `name_tpe`
                // (`tpe_name` never existed → was always undefined).
                tpe_name: el["name_tpe"],
                device_code: el["device_code"],
                speed: el["speed"],
                ip: el["ip"],
                payment_type: el["payment_type"],
                kiss: el["kiss"],
                reference: el[params.t("Reference")],
                general: el["general"] || {},
                terminal_type: el["terminal_type"],
                setup_type: el["setup_type"],
                timeout: el["timeout"],
                payment_method: el["payment_method"],
                mode: el["mode"] ?? "",
                // Adyen / type-specific configuration — previously dropped, so an
                // associated Adyen monetic lost its full configured setup.
                webhook: el["webhook"],
                adyenIp: el["adyenIp"],
                idIntegrator: el["idIntegrator"],
                currency: el["currency"],
                autoValidate: el["autoValidate"],
                adyenConfig: el["adyenConfig"],
            };
        }
    });

    const cashManagementToAssociate: Record<string, any> = {};
    params.cashManagements.forEach((el: any) => {
        if (isNewPeripheral(el["Id"], disassocCashIds)) {
            cashManagementToAssociate[el["Id"]] = {
                id_cash_management: el["Id"],
                designation: el[params.t("Designation")],
                mark: el[params.t("Mark")],
                port_number: el["port_number"],
                reference: el["Référence"],
                note: el["Remarque"],
                server_directory: el["server_directory"],
                activate: "",
                user: el["user"],
                ip: el["ip"],
                password: el["password"],
                pos_id: el["pos_id"],
                coin_loading_alert: {},
                empty_coin_alert: {},
                coin_status: {},
                collection_alert: {},
                withdrawal_Reason: {},
                maintenance: {},
                cash_Fund: {},
                accepted_denomination: {
                    "5.00 €": false,
                    "10.00 €": false,
                    "20.00 €": false,
                    "50.00 €": false,
                    "100.00 €": false,
                    "200.00 €": false,
                    "500.00 €": false,
                },
            };
        }
    });

    const hasDataToProcess =
        Object.keys(printersToAssociate).length > 0 ||
        Object.keys(moneticsToAssociate).length > 0 ||
        Object.keys(cashManagementToAssociate).length > 0 ||
        Object.keys(printersToDisassociate).length > 0 ||
        Object.keys(moneticsToDisassociate).length > 0 ||
        Object.keys(cashManagementToDisassociate).length > 0;

    if (!hasDataToProcess) return null;

    const requestData: AssociatePeripheralsPayload = {
        userId: params.userID,
        uuidUser: localStorage.getItem("uuidUser"),
        shopId: shopId,
        franchiseId: params.franchiseID,
        appId: APP_ID,
        operatorId: params.operatorID,
        appId_children: [row["appId"]],
        designation: row.Application,
        associate: {
            printers: printersToAssociate,
            monetics: moneticsToAssociate,
            cashManagements: cashManagementToAssociate,
        },
        disassociate: {
            printers: printersToDisassociate,
            monetics: moneticsToDisassociate,
            cashManagements: cashManagementToDisassociate,
        },
    };

    return postJSON(fetchGlobal, PERIPHERALS_URL, requestData);
}

// ---------------------------------------------------------------------------
// Monetic setting
// ---------------------------------------------------------------------------

export async function saveMoneticSetting(
    fetchGlobal: FetchGlobalFn,
    params: {
        userId: string;
        shopId: string;
        franchiseId: string;
        appId_children: string;
        appId: string;
        moneticId: string;
        additionalStep: boolean;
        printThePaymentTicket: boolean;
    }
) {
    const payload: MoneticSettingPayload = {
        userId: params.userId,
        shopId: params.shopId,
        franchiseId: params.franchiseId,
        appId_children: params.appId_children,
        appId: params.appId,
        moneticId: params.moneticId,
        data: {
            general: {
                Choice_of_payment: { additional_step: params.additionalStep },
                credit_card_ticket: {
                    print_payment_ticket: params.printThePaymentTicket,
                },
            },
        },
    };

    return postJSON(fetchGlobal, MONETIC_URL, payload);
}

// ---------------------------------------------------------------------------
// Currencies
// ---------------------------------------------------------------------------

export async function fetchCurrencies(fetchGlobal: FetchGlobalFn) {
    const result: any = await fetchGlobal(CURRENCIES_URL, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
    });
    return result.data.map((el: any) => ({
        label: el.key,
        value: el.Symbole,
    }));
}
